import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';

export interface Permission {
    id: number;
    name: string;
    role: string;
    label: string;
    recommend: boolean;
}

class PermissionEditor {
    public permission_id: number | null = null;
    public permission_name: string = '';
    public permission_role: string = '';
    public permission_label: string = '';
    public permission_recommend: boolean = false;

    constructor(permission: Permission | null = null, role: string | null = null) {
        makeObservable(this, {
            permission_name: observable,
            permission_role: observable,
            permission_label: observable,
            permission_recommend: observable,
            name: computed,
            role: computed,
            label: computed,
            recommend: computed,
            isNew: computed,
            isValid: computed,
        });

        if (permission) {
            this.permission_id = permission.id;
            this.permission_name = permission.name;
            this.permission_role = permission.role;
            this.permission_label = permission.label;
            this.permission_recommend = permission.recommend;
        } else if (role) {
            this.permission_role = role;
        }
    }

    get isNew(): boolean {
        return this.permission_id === null;
    }

    get isValid(): boolean {
        return (this.permission_name?.length || 0) >= 3 && (this.permission_role?.length || 0) > 0;
    }

    get name(): string {
        return this.permission_name || '';
    }

    set name(value: string) {
        runInAction(() => {
            this.permission_name = value || '';
        });
    }

    get role(): string {
        return this.permission_role || '';
    }

    set role(value: string) {
        runInAction(() => {
            this.permission_role = value || '';
        });
    }

    get label(): string {
        return this.permission_label || '';
    }

    set label(value: string) {
        runInAction(() => {
            this.permission_label = value || '';
        });
    }

    get recommend(): boolean {
        return this.permission_recommend;
    }

    set recommend(value: boolean) {
        runInAction(() => {
            this.permission_recommend = value;
        });
    }
}

class PermissionManagementStore {
    private sdk: ApplicationSdk;

    public permissions: Permission[] = [];
    public availableRoles: string[] = [];
    public loading: boolean = false;
    public editor: PermissionEditor | null = null;
    public selectedRole: string = 'employee'; // Default to employee role

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            permissions: observable,
            availableRoles: observable,
            loading: observable,
            editor: observable,
            selectedRole: observable,
            filteredPermissions: computed,
            groupedPermissions: computed,
            loadPermissions: action,
            openEditor: action,
            closeEditor: action,
            savePermission: action,
            deletePermission: action,
            setSelectedRole: action,
        });
        this.sdk = sdk;
    }

    public async init(): Promise<void> {
        await this.loadPermissions();
    }

    public async loadPermissions(): Promise<void> {
        runInAction(() => {
            this.loading = true;
        });

        try {
            const response = await this.sdk.fetchApi('admin', 'permissions/query', {});

            if (response?.status === 200) {
                runInAction(() => {
                    this.permissions = response.json?.data || [];
                });
            }
        } catch (error) {
            console.error('Error loading permissions:', error);
            this.sdk.showErrorToast('Fehler beim Laden der Berechtigungen');
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    public async savePermission(): Promise<void> {
        if (!this.editor || !this.editor.isValid) {
            return;
        }

        runInAction(() => {
            this.loading = true;
        });

        try {
            const isNew = this.editor.isNew;
            const permissionData = {
                name: this.editor.name,
                role: this.editor.role,
                label: this.editor.label,
                is_recommend: this.editor.recommend,
            };

            const response = isNew
                ? await this.sdk.fetchApi('admin', 'permissions/add', permissionData)
                : await this.sdk.fetchApi('admin', 'permissions/update', {
                      ...permissionData,
                      permission_id: this.editor.permission_id,
                  });

            if (response?.status === 201 || response?.status === 200) {
                await this.loadPermissions();
                this.closeEditor();
                this.sdk.showSuccessToast(
                    isNew
                        ? 'Berechtigung erfolgreich erstellt'
                        : 'Berechtigung erfolgreich aktualisiert'
                );
            } else {
                throw new Error(response?.json?.error || 'Unbekannter Fehler');
            }
        } catch (error: any) {
            console.error('Error saving permission:', error);
            this.sdk.showErrorToast(error.message || 'Fehler beim Speichern der Berechtigung');
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    public async deletePermission(permission: Permission): Promise<any> {
        runInAction(() => {
            this.loading = true;
        });

        try {
            const response = await this.sdk.fetchApi('admin', 'permissions/delete', {
                permission_id: permission.id,
            });

            if (response?.status === 200) {
                await this.loadPermissions();
                this.sdk.showSuccessToast('Berechtigung erfolgreich gelöscht');
                return response; // Return the MatenRequestResponse
            } else {
                const error = response?.json?.error || 'Unbekannter Fehler';
                this.sdk.showErrorToast(error);
                return { error }; // Return error response
            }
        } catch (error: any) {
            console.error('Error deleting permission:', error);
            const errorMessage = error.message || 'Fehler beim Löschen der Berechtigung';
            this.sdk.showErrorToast(errorMessage);
            return { error: errorMessage }; // Return error response
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    public openEditor(permission: Permission | null = null, role: string | null = null): void {
        runInAction(() => {
            this.editor = new PermissionEditor(permission, role);
        });
    }

    public closeEditor(): void {
        runInAction(() => {
            this.editor = null;
        });
    }

    public setSelectedRole(role: string): void {
        runInAction(() => {
            this.selectedRole = role;
        });
    }

    get filteredPermissions(): Permission[] {
        return this.permissions.filter(p => p.role === this.selectedRole);
    }

    get groupedPermissions(): { [role: string]: Permission[] } {
        const grouped: { [role: string]: Permission[] } = {};

        this.filteredPermissions.forEach(permission => {
            if (!grouped[permission.role]) {
                grouped[permission.role] = [];
            }
            grouped[permission.role].push(permission);
        });

        return grouped;
    }
}

export default PermissionManagementStore;
